import type React from "react";
import {
  type Option,
  SelectComboField,
  type SelectComboFieldProps,
} from "./SelectCombo";

export interface SelectListWrapperProps<T>
  extends Omit<SelectComboFieldProps, "options"> {
  /** Items to turn into options. */
  items: T[];
  /** Map one item to a select Option. */
  toOption: (item: T) => Option;
  /** Loading state from an async data source. Omit for static lists. */
  loading?: boolean;
  /** Data-source error — surfaced as the placeholder. Distinct from the field's
   * own validation `error` string (which still passes through to the field). */
  dataError?: Error | null;
  /** Sentinel options prepended before the mapped items (e.g. "All"/"None"). */
  leadingOptions?: Option[];
  /** Placeholder text for the loading / empty states. */
  labels?: { empty?: string; loading?: string };
}

/**
 * Generic wrapper over {@link SelectComboField} that owns the boilerplate the
 * ~25 hand-written select wrappers repeat: map a data source to `Option[]`,
 * disable the field while loading, and pick a placeholder from the
 * loading/error/empty state. Static lists pass just `items`/`toOption`; async
 * lists also pass `loading`/`error`.
 */
export function SelectListWrapper<T>(
  props: SelectListWrapperProps<T>,
): React.ReactElement {
  const {
    items,
    toOption,
    loading = false,
    dataError = null,
    leadingOptions = [],
    labels,
    placeholder,
    disabled,
    ...rest
  } = props;

  const options: Option[] = [...leadingOptions, ...items.map(toOption)];

  const placeholderText = loading
    ? (labels?.loading ?? "Loading")
    : dataError
      ? (dataError.message ?? "Error")
      : items.length === 0
        ? (labels?.empty ?? "No options")
        : placeholder;

  return (
    <SelectComboField
      {...rest}
      options={options}
      placeholder={placeholderText}
      disabled={loading || disabled}
    />
  );
}
